import {
  Box,
  Button,
  Flex,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  useDisclosure,
  Heading,
} from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import * as Sentry from '@sentry/react';
import { Formik, FormikProps, getIn } from 'formik';
import * as _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { getIterationKey } from '../../common';
import { DeleteConfirmationModal } from '../../delete/DeleteArrayPropertyButton';
import { defaultTheme, lightTheme } from '../../edit/MonacoEditor/ui/common';
import { CovenantResultContainer } from './CovenantValueView';
import {
  AddThresholdValueField,
  createThresholdIntermediateFieldShell,
  DeleteThresholdValueField,
  getNewIntermediateFieldsWithCovenantResultFormula,
  getThresholdLevel,
  getThresholdType,
  isThresholdValueIntermediateField,
  ThresholdType,
  thresholdTypeOptions,
  MAX_THRESHOLD_LEVEL,
} from './thresholdValueUtils';
import { colors } from 'packages/common-frontend/colors';
import { FormulaContainer } from 'packages/common-frontend/components/FormulaContainer';
import {
  AddTextButton,
  DeleteIconButton,
  EditIconButton,
} from 'packages/common-frontend/components/IconButton';
import { InfoAlertWithDescription } from 'packages/common-frontend/components/InfoAlertWithDescription';
import { Select } from 'packages/common-frontend/components/Select';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { WipConfig } from 'packages/creditAgreements/ui/types';

type AddCovenantThresholdLevelModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onClickAdd: (params: { thresholdFormulaInput: string }) => Promise<void>;
  initialThresholdFormula: string;
  isAddLoading: boolean;
  nextThresholdLevel: number;
};

const AddCovenantThresholdLevelModal = ({
  isOpen,
  onClose,
  onClickAdd,
  initialThresholdFormula,
  isAddLoading,
  nextThresholdLevel,
}: AddCovenantThresholdLevelModalProps) => {
  const toast = useToast();

  const [thresholdFormula, setThresholdFormula] = useState<string | undefined>(
    initialThresholdFormula,
  );

  const onCloseModal = () => {
    onClose();
    setThresholdFormula(initialThresholdFormula);
  };

  // if level is unset, toast error and do not allow add
  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Threshold Level</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex mb="4">
            <Text fontWeight={600} mr="2">
              Threshold:
            </Text>
            <Text decoration="underline">Level {nextThresholdLevel}</Text>
          </Flex>
          <Text mb="16px" fontWeight={600}>
            Formula / Value
          </Text>
          <Box
            border="1px"
            borderColor={colors.gray[200]}
            borderRadius="8px"
            p="8px"
            overflow="auto"
            height="15vh"
            width="100%"
          >
            <Editor
              value={thresholdFormula}
              onChange={(value) => setThresholdFormula(value)}
              theme={lightTheme}
              options={{
                fixedOverflowWidgets: true,
                minimap: { enabled: false },
              }}
            />
          </Box>
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="aurora"
            variant="outline"
            borderWidth="2px"
            mr="32px"
            onClick={onCloseModal}
          >
            Cancel
          </Button>
          <Button
            colorScheme="aurora"
            color="white"
            onClick={async () => {
              if (thresholdFormula) {
                await onClickAdd({ thresholdFormulaInput: thresholdFormula });
                onCloseModal();
              } else {
                toast({
                  ...errorToastConfig,
                  id: 'add-threshold-modal-missing-info',
                  title: 'Fill in all required fields',
                });
              }
            }}
            isLoading={isAddLoading}
          >
            Add
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

type EditThresholdValueRowProps = {
  onCancel: Function;
  onSave: (params: {
    field: CreditAgreement.Config.CommonIntermediateField.IntermediateField;
    saveIdx: number;
    fullPropertyKey: string;
    configPropertyKey: string;
    setIsSaveLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  }) => Promise<void>;
  intermediateField: CreditAgreement.Config.CommonIntermediateField.IntermediateField;
  idx: number;
  operationMode?: CreditAgreement.OperationMode;
  configPropertyKey: string;
  fullPropertyKey: string;
  thresholdLevel: number;
};

export const EditThresholdValueRow = ({
  onCancel,
  onSave,
  intermediateField,
  idx,
  operationMode,
  configPropertyKey,
  fullPropertyKey,
  thresholdLevel,
}: EditThresholdValueRowProps) => {
  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false);
  return (
    <Formik
      initialValues={{
        field: intermediateField.field,
        config: {
          formula: intermediateField.config.formula,
          engineVersion: intermediateField.config.engineVersion,
          type: intermediateField.config.type,
        },
      }}
      onSubmit={async (props) => {
        try {
          await onSave({
            field: props,
            saveIdx: idx,
            configPropertyKey,
            fullPropertyKey,
            setIsSaveLoading: setIsSaving,
          });
        } catch (err: any) {
          Sentry.captureException(JSON.stringify(err, null, 2));
          toast({
            ...errorToastConfig,
            title:
              'Error saving credit agreement. Please try again, and contact your Finley representative if this persists.',
          });
        }
      }}
    >
      {(formProps: FormikProps<any>) => (
        <Tr>
          <Td verticalAlign="top">{`Level ${thresholdLevel}`}</Td>
          <Td>
            <Box
              border="1px"
              borderColor={colors.gray[200]}
              borderRadius="8px"
              p="8px"
              overflow="auto"
              height="15vh"
            >
              <Editor
                value={formProps.values.config.formula}
                onChange={(val) =>
                  formProps.setFieldValue('config.formula', val)
                }
                theme={operationMode === 'client' ? lightTheme : defaultTheme}
                options={{
                  fixedOverflowWidgets: true,
                  minimap: { enabled: false },
                }}
              />
            </Box>
          </Td>

          <Td verticalAlign="top">
            <Button
              w="100%"
              mb="16px"
              color="white"
              backgroundColor={colors.aurora[400]}
              onClick={() => {
                formProps.handleSubmit();
              }}
              isLoading={isSaving}
            >
              Save
            </Button>
            <Button
              w="100%"
              color={colors.aurora[400]}
              border="2px"
              borderRadius="8px"
              borderColor={colors.aurora[400]}
              backgroundColor="white"
              onClick={() => onCancel()}
            >
              Cancel
            </Button>
          </Td>
        </Tr>
      )}
    </Formik>
  );
};

type ThresholdIntermediateFieldViewProps = {
  intermediateField: CreditAgreement.Config.CommonIntermediateField.IntermediateField;
  wipConfig?: WipConfig;
  configPropertyKey: string;
  fullPropertyKey: string;
  idx: number;
  onSave: (params: {
    field: CreditAgreement.Config.CommonIntermediateField.IntermediateField;
    saveIdx: number;
    fullPropertyKey: string;
    configPropertyKey: string;
  }) => Promise<void>;
  thresholdLevel: number;
  onDeleteIntermediateField: (
    params: DeleteThresholdValueField,
  ) => Promise<void>;
  isDeleteLoading: boolean;
  canDelete: boolean;
};

const ThresholdIntermediateFieldView = ({
  wipConfig,
  intermediateField,
  configPropertyKey,
  fullPropertyKey,
  idx,
  onSave,
  thresholdLevel,
  onDeleteIntermediateField,
  isDeleteLoading,
  canDelete,
}: ThresholdIntermediateFieldViewProps) => {
  const [isIntermediateValueEditOn, setIsIntermediateValueEditOn] = useState(
    false,
  );
  const {
    isOpen: isDeleteConfirmationModalOpen,
    onOpen: onDeleteConfirmationModalOpen,
    onClose: onDeleteConfirmationModalClose,
  } = useDisclosure();

  if (!isIntermediateValueEditOn) {
    return (
      <Tr>
        <Td>{`Level ${thresholdLevel}`}</Td>
        <Td whiteSpace="pre-wrap">
          <FormulaContainer>
            {String(intermediateField.config.formula)}
          </FormulaContainer>
        </Td>
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <Td>
            <EditIconButton
              ariaLabel="edit threshold value"
              label="Edit Threshold Field"
              onClick={() => {
                setIsIntermediateValueEditOn(true);
              }}
            />
            {canDelete && (
              <>
                <DeleteIconButton
                  ariaLabel="delete"
                  label="Delete"
                  onClick={() => {
                    onDeleteConfirmationModalOpen();
                  }}
                />
                <DeleteConfirmationModal
                  isOpen={isDeleteConfirmationModalOpen}
                  onClose={onDeleteConfirmationModalClose}
                  onClickDelete={async () => {
                    await onDeleteIntermediateField({
                      wc: wipConfig,
                      indexToDelete: idx,
                      action: 'delete',
                    });
                  }}
                  isLoading={isDeleteLoading}
                />
              </>
            )}
          </Td>
        )}
      </Tr>
    );
  }

  return (
    <EditThresholdValueRow
      onCancel={() => setIsIntermediateValueEditOn(false)}
      onSave={onSave}
      intermediateField={intermediateField}
      idx={idx}
      operationMode={wipConfig?.operationMode}
      configPropertyKey={configPropertyKey}
      fullPropertyKey={fullPropertyKey}
      thresholdLevel={thresholdLevel}
    />
  );
};

type CovenantTypePrefix = 'portfolio' | 'financial';

type ThresholdIntermediateFieldsViewProps = {
  covenantType: CovenantTypePrefix;
  intermediateFields: CreditAgreement.Config.CommonIntermediateField.IntermediateField[];
  wipConfig?: WipConfig;
  intermediateFieldPropertyKey: string;
  configPropertyKey: string;
  onSave: (params: {
    field: CreditAgreement.Config.CommonIntermediateField.IntermediateField;
    saveIdx: number;
    fullPropertyKey: string;
    configPropertyKey: string;
  }) => Promise<void>;
};

export const ThresholdIntermediateFieldsView = ({
  covenantType,
  wipConfig,
  intermediateFieldPropertyKey,
  configPropertyKey,
  intermediateFields,
  onSave,
}: ThresholdIntermediateFieldsViewProps) => {
  const {
    isOpen: isAddModalOpen,
    onOpen: onAddModalOpen,
    onClose: onAddModalClose,
  } = useDisclosure();
  const toast = useToast();

  const [thresholdType, setThresholdType] = useState<ThresholdType | undefined>(
    getThresholdType(covenantType, intermediateFields) ?? undefined,
  );
  const [isAddLoading, setIsAddLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isThresholdTypeLoading, setIsThresholdTypeLoading] = useState(false);

  // This is used to order THRESHOLD VALUE FIELDS for display purposes only
  // so that a user can easily see thresholds from level 1 -> higher.
  // We should not mutate the order of intermediate fields in the actual CA.
  // That must be done intentionally and separately via the Draggable Rows UI.
  const orderedThresholdFields = useMemo(() => {
    const thresholdFields = intermediateFields.filter(({ field }) =>
      isThresholdValueIntermediateField(field),
    );

    return thresholdFields.sort((a, b) => {
      const aLevel = getThresholdLevel(a.field);
      if (aLevel === null) return 1;

      const bLevel = getThresholdLevel(b.field);
      if (bLevel === null) return -1;

      return aLevel - bLevel;
    });
  }, [intermediateFields]);

  const addOrDeleteThresholdValueField = useCallback(
    async (params: AddThresholdValueField | DeleteThresholdValueField) => {
      const { wc, action } = params;

      const newIntermediateFields: CreditAgreement.Config.CommonIntermediateField.IntermediateField[] = _.cloneDeep(
        getIn(wc.formProps.values, intermediateFieldPropertyKey),
      );

      if (action === 'add') {
        // create new threshold level intermediate field shell and add to beginning of list
        const newIntermediateField = createThresholdIntermediateFieldShell(
          params.thresholdLevel,
          params.thresholdFormula,
        );

        newIntermediateFields.unshift(newIntermediateField);
      } else if (action === 'delete') {
        // delete specified intermediate field
        newIntermediateFields.splice(params.indexToDelete, 1);
      }

      if (!thresholdType) {
        errorToastConfig.title = 'Select threshold type';
        toast(errorToastConfig);

        return;
      }

      const newIntermediateFieldsWithUpdatedResultsFormula = getNewIntermediateFieldsWithCovenantResultFormula(
        {
          covenantType,
          intermediateFields: newIntermediateFields,
          thresholdType,
        },
      );

      // set with new intermediate fields for both the newly-added threshold and covenantResult
      await wc.saveAndUpdateConfigCreditAgreementWip({
        wipConfig: wc,
        propertyKey: intermediateFieldPropertyKey,
        updatedProperty: newIntermediateFieldsWithUpdatedResultsFormula,
        setIsSaveLoading:
          action === 'add' ? setIsAddLoading : setIsDeleteLoading,
      });
    },
    [covenantType, intermediateFieldPropertyKey, thresholdType, toast],
  );

  const existingThresholdLevels = useMemo(() => {
    const thresholdLevelFields = _.filter(
      intermediateFields,
      (intermediateField) => {
        return isThresholdValueIntermediateField(intermediateField.field);
      },
    );

    return _.compact(
      _.map(thresholdLevelFields, (thresholdLevelField) =>
        getThresholdLevel(thresholdLevelField.field),
      ),
    );
  }, [intermediateFields]);

  const onChangeThresholdType = useCallback(
    async (wc: WipConfig, newThresholdType: ThresholdType | undefined) => {
      if (!newThresholdType) {
        errorToastConfig.title = 'Select threshold type';
        toast(errorToastConfig);

        return;
      }

      setThresholdType(newThresholdType);

      // generate updated covenantResult field
      const newIntermediateFields = getNewIntermediateFieldsWithCovenantResultFormula(
        {
          covenantType,
          intermediateFields: getIn(
            wc.formProps.values,
            intermediateFieldPropertyKey,
          ),
          thresholdType: newThresholdType,
        },
      );

      // set with new intermediate fields for both the newly-added threshold and covenantResult
      await wc.saveAndUpdateConfigCreditAgreementWip({
        wipConfig: wc,
        propertyKey: intermediateFieldPropertyKey,
        updatedProperty: newIntermediateFields,
        setIsSaveLoading: setIsThresholdTypeLoading,
      });
    },
    [covenantType, intermediateFieldPropertyKey, toast],
  );

  const isClientWipMode = wipConfig?.operationMode === 'client';

  return (
    <>
      <Heading as="h3" fontSize="20px" fontWeight={600} mb="16px" mt="64px">
        {commonUtils.toTitleCase(covenantType)} Covenant Result
      </Heading>
      <Box
        borderWidth="1px"
        borderColor={colors.gray[200]}
        borderRadius="8px"
        p="32px"
      >
        <CovenantResultContainer
          covenantType={covenantType}
          wipConfig={isClientWipMode ? undefined : wipConfig} // client can only view formula, admin can edit formula directly
          intermediateFields={intermediateFields}
          configPropertyKey={configPropertyKey}
          basePropertyKey={intermediateFieldPropertyKey}
          onSave={onSave}
        />

        <Box width="100%" mt="64px">
          <Box mb="24px">
            <Heading as="h3" fontSize="20px" fontWeight={600} mb="24px">
              Threshold
            </Heading>
            {!isClientWipMode && (
              <Box mb="24px">
                <InfoAlertWithDescription
                  description={`Updating threshold values will automatically overwrite the existing ${covenantType} covenant result formula`}
                />
              </Box>
            )}
            <Text color={colors.black[70]} mb="24px">
              Set a minimum or maximum as the type of threshold.
            </Text>
            {wipConfig?.isMutateAllowed(intermediateFieldPropertyKey) && (
              <Flex alignItems="center">
                <Box width="300px">
                  <Select
                    value={thresholdType}
                    options={thresholdTypeOptions}
                    onChange={(newVal) =>
                      onChangeThresholdType(wipConfig, newVal)
                    }
                    isDisabled={isThresholdTypeLoading}
                  />
                </Box>
                <Spacer />

                <AddTextButton
                  ariaLabel="add threshold"
                  label="Add Threshold"
                  onClick={() => {
                    onAddModalOpen();
                  }}
                  isDisabled={
                    !thresholdType ||
                    existingThresholdLevels.length + 1 > MAX_THRESHOLD_LEVEL
                  }
                  tooltip={
                    !thresholdType
                      ? 'Select threshold type to add threshold'
                      : ''
                  }
                />
                {thresholdType && (
                  <AddCovenantThresholdLevelModal
                    isOpen={isAddModalOpen}
                    onClose={onAddModalClose}
                    onClickAdd={async ({ thresholdFormulaInput }) => {
                      await addOrDeleteThresholdValueField({
                        wc: wipConfig,
                        thresholdLevel: existingThresholdLevels.length + 1,
                        thresholdFormula: thresholdFormulaInput,
                        action: 'add',
                      });
                    }}
                    initialThresholdFormula='"PLACEHOLDER"'
                    isAddLoading={isAddLoading}
                    nextThresholdLevel={existingThresholdLevels.length + 1}
                  />
                )}
              </Flex>
            )}
          </Box>

          {intermediateFields.length === 0 && (
            <Text p="3" border="1px" borderColor="gray.700">
              There are currently no threshold values configured.
            </Text>
          )}
          {intermediateFields.length > 0 && (
            <Box overflow="scroll" maxHeight="50vh" borderWidth="1px">
              <Table>
                <Thead>
                  <Tr position="sticky" top="0" zIndex={1}>
                    <Th w="30%">Level</Th>
                    <Th w="55%">Threshold</Th>
                    {wipConfig && <Th />}
                  </Tr>
                </Thead>
                <Tbody>
                  {orderedThresholdFields.map((thresholdField, fieldIdx) => {
                    const { field } = thresholdField;
                    const id = getIterationKey(thresholdField);
                    const thresholdLevel = getThresholdLevel(field);

                    if (!_.isNumber(thresholdLevel)) {
                      return null;
                    }

                    // Represents the idx of the threshold intermediate field before
                    // being reordered for UI purposes. Need this idx in case we want
                    // to mutate the property within CA
                    const originalIndexBeforeOrdering = intermediateFields.findIndex(
                      (intField) => intField.field === field,
                    );

                    // Can only delete the latest available threshold to help prevent
                    // threshold levels skipping. Also will always need at least 1
                    // threshold level, so prevent deleting the first one
                    const canDelete =
                      fieldIdx > 0 &&
                      fieldIdx === orderedThresholdFields.length - 1;

                    return (
                      <ThresholdIntermediateFieldView
                        key={`${id}-${field}`}
                        configPropertyKey={configPropertyKey}
                        intermediateField={thresholdField}
                        wipConfig={wipConfig}
                        fullPropertyKey={`${intermediateFieldPropertyKey}[${originalIndexBeforeOrdering}]`}
                        idx={originalIndexBeforeOrdering}
                        onSave={onSave}
                        thresholdLevel={thresholdLevel}
                        onDeleteIntermediateField={async (
                          params: DeleteThresholdValueField,
                        ) => addOrDeleteThresholdValueField(params)}
                        isDeleteLoading={isDeleteLoading}
                        canDelete={canDelete}
                      />
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};
